import type { Block, ColumnsBlock } from './types'
import { emptyText, ensureTrailingText, isBlockEmpty, isEditable, isOutOfFlow, newBlockId } from './blocks'

// Columns (Niki, 10/1): marker blocks in the flat list. The column blocks
// between a `start` and its `end` sit side by side, one cell per run between
// markers: [start, …cell 1…, break, …cell 2…, (break, …cell 3…,) end]. The
// count is never stored — it's the breaks plus one — so it can't disagree
// with them. normalizeColumns() repairs anything an edit leaves out of shape.

export const MAX_COLUMNS = 3

export function isColumnsMarker(b: Block | undefined): b is ColumnsBlock {
  return b?.type === 'columns'
}

export interface ColumnsCell {
  /** The marker the cell starts after (the group's start, or a break). */
  markerId: string
  /** Its blocks: indexes [from, to). */
  from: number
  to: number
}

export interface ColumnsGroup {
  startId: string
  /** Index of the start marker. */
  start: number
  /** Index of the end marker. */
  end: number
  cells: ColumnsCell[]
}

/** Every well-formed group in the document, in order. */
export function columnGroups(blocks: Block[]): ColumnsGroup[] {
  const out: ColumnsGroup[] = []
  let open: { start: number; cells: ColumnsCell[] } | null = null
  blocks.forEach((b, i) => {
    if (!isColumnsMarker(b)) return
    if (b.part === 'start') {
      open = { start: i, cells: [{ markerId: b.id, from: i + 1, to: i + 1 }] }
    } else if (open) {
      open.cells[open.cells.length - 1].to = i
      if (b.part === 'break') {
        open.cells.push({ markerId: b.id, from: i + 1, to: i + 1 })
      } else {
        out.push({ startId: blocks[open.start].id, start: open.start, end: i, cells: open.cells })
        open = null
      }
    }
  })
  return out
}

/** The group a block index sits in (its markers count as in it), or null. */
export function groupAt(blocks: Block[], index: number): ColumnsGroup | null {
  return columnGroups(blocks).find((g) => index >= g.start && index <= g.end) ?? null
}

/** The group `id` sits in, or null. */
export function groupOf(blocks: Block[], id: string | null): ColumnsGroup | null {
  if (!id) return null
  const i = blocks.findIndex((b) => b.id === id)
  return i < 0 ? null : groupAt(blocks, i)
}

/** A new group of `count` cells, each with a paragraph to write in. */
export function columnsBlocks(count: number, newId: () => string = newBlockId): Block[] {
  const n = Math.min(MAX_COLUMNS, Math.max(2, Math.round(count)))
  const out: Block[] = [{ id: newId(), type: 'columns', part: 'start' }]
  for (let c = 0; c < n; c++) {
    if (c > 0) out.push({ id: newId(), type: 'columns', part: 'break' })
    out.push({ id: newId(), type: 'text', html: '' })
  }
  out.push({ id: newId(), type: 'columns', part: 'end' })
  return out
}

/**
 * 2 or 3 cells. Adding one puts an empty cell on the end; taking one away
 * joins the last cell's blocks onto the one before it, so nothing is lost.
 */
export function setColumnCount(blocks: Block[], startId: string, count: number, newId: () => string = newBlockId): Block[] {
  const g = columnGroups(blocks).find((x) => x.startId === startId)
  const n = Math.min(MAX_COLUMNS, Math.max(2, Math.round(count)))
  if (!g || g.cells.length === n) return blocks
  const out = blocks.slice()
  if (n > g.cells.length) {
    const extra: Block[] = []
    for (let c = g.cells.length; c < n; c++) extra.push({ id: newId(), type: 'columns', part: 'break' }, { id: newId(), type: 'text', html: '' })
    out.splice(g.end, 0, ...extra)
    return out
  }
  const drop = new Set(g.cells.slice(n).map((c) => c.markerId))
  return out.filter((b) => !drop.has(b.id))
}

/** Take the columns away and keep everything in them, one cell after another. */
export function unwrapColumns(blocks: Block[], startId: string): Block[] {
  const g = columnGroups(blocks).find((x) => x.startId === startId)
  if (!g) return blocks
  return ensureTrailingText(blocks.filter((b, i) => !(i >= g.start && i <= g.end && isColumnsMarker(b))))
}

/** Delete the columns and everything in them. */
export function deleteColumns(blocks: Block[], startId: string): Block[] {
  const g = columnGroups(blocks).find((x) => x.startId === startId)
  if (!g) return blocks
  return ensureTrailingText([...blocks.slice(0, g.start), ...blocks.slice(g.end + 1)])
}

/** Nothing written in any cell: only empty paragraphs. */
export function isColumnsEmpty(blocks: Block[], g: ColumnsGroup): boolean {
  return blocks.slice(g.start + 1, g.end).every((b) => isColumnsMarker(b) || (b.type === 'text' && isBlockEmpty(b)))
}

/** Nothing in this cell but empty paragraphs (a margin note or a card counts as something). */
export function isCellEmpty(blocks: Block[], cell: ColumnsCell): boolean {
  return blocks.slice(cell.from, cell.to).every((b) => b.type === 'text' && isBlockEmpty(b))
}

// ── Widths: the gutter between cells drags to resize them ────────────────

/** The narrowest a column can be dragged: a share of the row. */
export const MIN_COLUMN_SHARE = 0.2

/**
 * How narrow the gutter lets a column get, as in Composer v3: 30% of an
 * even share (15% of the row for two columns, 10% for three), so an image
 * column can be made small beside the text.
 */
export function columnMinShare(n: number): number {
  return 0.3 / Math.max(1, n)
}

export function equalWidths(n: number): number[] {
  return Array.from({ length: n }, () => 1 / n)
}

/** The stored widths of a group's start marker, when they fit `n` cells; else null (equal columns). */
export function columnWidths(start: Block | undefined, n: number): number[] | null {
  const w = isColumnsMarker(start) && start.part === 'start' ? start.widths : undefined
  if (!w || w.length !== n || !w.every((x) => Number.isFinite(x) && x > 0)) return null
  const sum = w.reduce((a, b) => a + b, 0)
  return w.map((x) => x / sum)
}

/** The grid's columns for these widths ("minmax(0, 0.6fr) minmax(0, 0.4fr)"), or undefined for equal ones. */
export function columnsTemplate(widths: number[] | null): string | undefined {
  return widths ? widths.map((w) => `minmax(0, ${+w.toFixed(4)}fr)`).join(' ') : undefined
}

/** A group's grid, inline: its cell count, and the widths its gutters were dragged to (composer and reader). */
export function columnsGridStyle(start: Block | undefined, n: number): Record<string, string | number> {
  const tpl = columnsTemplate(columnWidths(start, n))
  return tpl ? { '--n': n, '--wr-cols-tpl': tpl } : { '--n': n }
}

/**
 * Where cell `c` (0-based) of `n` sits in its group, for the margins (Niki,
 * 10/5: a margin note in a column still goes in the page's margin): the
 * shares of the row before and after it, and the gaps either side, as the
 * CSS variables index.css .wr-cell turns into its distance to each edge.
 */
export function cellMarginStyle(start: Block | undefined, n: number, c: number): Record<string, number> {
  const w = columnWidths(start, n) ?? equalWidths(n)
  const sum = (xs: number[]) => +xs.reduce((a, b) => a + b, 0).toFixed(4)
  return { '--wr-cb': sum(w.slice(0, c)), '--wr-ca': sum(w.slice(c + 1)), '--wr-gb': c, '--wr-ga': n - 1 - c }
}

/**
 * Move the gutter before cell `boundary` (1 … n−1) by `delta`, a share of the
 * row: the cells either side of it grow and shrink together, and neither goes
 * under MIN_COLUMN_SHARE. The others keep their width.
 */
export function resizeBoundary(widths: number[], boundary: number, delta: number, min = MIN_COLUMN_SHARE): number[] {
  if (boundary < 1 || boundary >= widths.length) return widths
  const a = widths[boundary - 1]
  const b = widths[boundary]
  const d = Math.max(min - a, Math.min(b - min, delta))
  const out = widths.slice()
  out[boundary - 1] = +(a + d).toFixed(4)
  out[boundary] = +(b - d).toFixed(4)
  return out
}

/** Store a group's widths (null: equal again). */
export function setColumnWidths(blocks: Block[], startId: string, widths: number[] | null): Block[] {
  return blocks.map((b) => {
    if (b.id !== startId || !isColumnsMarker(b)) return b
    const { widths: _old, ...rest } = b
    return widths ? { ...rest, widths } : rest
  })
}

// ── Taking a cell away ────────────────────────────────────────────────────

/**
 * Take one cell away with everything in it. The others keep their share of
 * the row; a group left with one cell goes back to the page's flow.
 */
export function removeCell(blocks: Block[], markerId: string): Block[] {
  const g = columnGroups(blocks).find((x) => x.cells.some((c) => c.markerId === markerId))
  if (!g) return blocks
  if (g.cells.length < 2) return unwrapColumns(blocks, g.startId)
  const ci = g.cells.findIndex((c) => c.markerId === markerId)
  const cell = g.cells[ci]
  // The first cell goes with the break after it (the start stays); any other with its own break.
  const [from, to] = ci === 0 ? [cell.from, g.cells[1].from] : [cell.from - 1, cell.to]
  const start = blocks[g.start]
  const widths = columnWidths(start, g.cells.length)
  let out = [...blocks.slice(0, from), ...blocks.slice(to)]
  if (widths) {
    const rest = widths.filter((_, i) => i !== ci)
    const sum = rest.reduce((a, b) => a + b, 0)
    out = setColumnWidths(out, start.id, rest.length > 1 ? rest.map((w) => +(w / sum).toFixed(4)) : null)
  }
  return normalizeColumns(ensureTrailingText(out))
}

/**
 * After an edit took something out of a cell (a delete, a drag elsewhere):
 * a cell that had something in it and now has nothing goes, so an emptied
 * column folds back instead of standing there blank. Cells that were empty
 * already (a fresh insert, still to be written in) stay.
 */
export function collapseEmptiedCells(before: Block[], after: Block[]): Block[] {
  const had = new Set<string>()
  for (const g of columnGroups(before)) for (const c of g.cells) if (!isCellEmpty(before, c)) had.add(c.markerId)
  if (!had.size) return after
  const done = new Set<string>()
  let out = after
  for (;;) {
    const emptied = columnGroups(out)
      .flatMap((g) => g.cells)
      .find((c) => had.has(c.markerId) && !done.has(c.markerId) && isCellEmpty(out, c))
    if (!emptied) return out
    done.add(emptied.markerId)
    out = removeCell(out, emptied.markerId)
  }
}

/**
 * Put the markers back in shape after any edit. In one pass: a start inside
 * an open group, or a break or end outside one, is dropped (no nesting, no
 * strays); a fourth cell joins the third; a group with no breaks, or left
 * open at the end, is unwrapped (or closed, when it has cells); and every
 * marker is followed by a paragraph when nothing column-like follows it, so
 * each cell has a line to write in and asides never hang from a marker.
 * Returns the same array when nothing needed fixing.
 */
export function normalizeColumns(blocks: Block[], newId: () => string = newBlockId): Block[] {
  if (!blocks.some(isColumnsMarker)) return blocks
  let changed = false
  const out: Block[] = []
  let open: number | null = null
  let breaks = 0
  for (const b of blocks) {
    if (!isColumnsMarker(b)) {
      out.push(b)
      continue
    }
    if (b.part === 'start') {
      if (open !== null) {
        changed = true
        continue
      }
      open = out.length
      breaks = 0
      out.push(b)
    } else if (b.part === 'break') {
      if (open === null || breaks >= MAX_COLUMNS - 1) {
        changed = true
        continue
      }
      breaks++
      out.push(b)
    } else {
      if (open === null) {
        changed = true
        continue
      }
      if (breaks === 0) {
        out.splice(open, 1)
        changed = true
      } else {
        out.push(b)
      }
      open = null
    }
  }
  if (open !== null) {
    changed = true
    if (breaks === 0) out.splice(open, 1)
    else out.push({ id: newId(), type: 'columns', part: 'end' })
  }
  // A line to write in after every marker that isn't followed by a column block.
  for (let i = out.length - 1; i >= 0; i--) {
    if (!isColumnsMarker(out[i])) continue
    const next = out[i + 1]
    if (next && !isOutOfFlow(next) && !isColumnsMarker(next)) continue
    out.splice(i + 1, 0, emptyText())
    changed = true
  }
  return changed ? out : blocks
}

/**
 * What Backspace at the very start of the column block at `index` should do
 * about columns. At the top of a cell it stays put (it would merge across a
 * cell's edge), unless every cell is empty — then the columns go, undoing an
 * insert — or this cell is: then that column goes, and the caret goes to the
 * end of the cell before it (the start of the one after, for the first).
 * Just after a group, it steps into the end of the last cell.
 */
export type BackspaceAt =
  | { kind: 'normal' }
  | { kind: 'stay' }
  | { kind: 'removeGroup'; startId: string }
  | { kind: 'removeCell'; markerId: string; focus: { id: string; at: 'start' | 'end' } | null }
  | { kind: 'caret'; id: string; removeSelf: boolean }

export function backspaceAt(blocks: Block[], index: number, empty: boolean): BackspaceAt {
  let a = index - 1
  while (a >= 0 && isOutOfFlow(blocks[a])) a--
  const prev = blocks[a]
  if (!isColumnsMarker(prev)) return { kind: 'normal' }
  const g = groupAt(blocks, a)
  if (!g) return { kind: 'normal' }
  if (prev.part === 'end') {
    const last = g.cells[g.cells.length - 1]
    const target = blocks.slice(last.from, last.to).reverse().find(isEditable)
    return target ? { kind: 'caret', id: target.id, removeSelf: empty } : { kind: 'stay' }
  }
  if (isColumnsEmpty(blocks, g)) return { kind: 'removeGroup', startId: g.startId }
  const ci = g.cells.findIndex((c) => c.markerId === prev.id)
  const cell = g.cells[ci]
  if (!empty || !cell || !isCellEmpty(blocks, cell)) return { kind: 'stay' }
  const before = g.cells[ci - 1]
  const after = g.cells[ci + 1]
  const end = before && blocks.slice(before.from, before.to).reverse().find(isEditable)
  const start = !end && after ? blocks.slice(after.from, after.to).find(isEditable) : undefined
  return {
    kind: 'removeCell',
    markerId: prev.id,
    focus: end ? { id: end.id, at: 'end' } : start ? { id: start.id, at: 'start' } : null,
  }
}

/** Delete at the end of the block at `index` would pull a block across a column marker. */
export function deleteForwardBlocked(blocks: Block[], index: number): boolean {
  let j = index + 1
  while (j < blocks.length && isOutOfFlow(blocks[j])) j++
  return isColumnsMarker(blocks[j])
}

/**
 * Enter on an empty last paragraph of the last cell leaves the columns: the
 * caret goes to the paragraph after them (this one moves there, unless an
 * empty one is waiting already). Returns the new list and where the caret
 * goes, or null when that isn't the case (Enter does what it always does).
 */
export function leaveColumns(blocks: Block[], index: number): { blocks: Block[]; focusId: string } | null {
  const b = blocks[index]
  if (!b || b.type !== 'text' || !isBlockEmpty(b)) return null
  const g = groupAt(blocks, index)
  if (!g) return null
  const last = g.cells[g.cells.length - 1]
  if (index < last.from) return null
  // The last column block of the cell, and not the only one in it.
  const columnBlocks = blocks.slice(last.from, last.to).filter((x) => !isOutOfFlow(x))
  if (columnBlocks.length < 2 || columnBlocks[columnBlocks.length - 1].id !== b.id) return null
  const out = blocks.filter((x) => x.id !== b.id)
  const end = out.findIndex((x) => x.id === blocks[g.end].id)
  const after = out[end + 1]
  if (after && after.type === 'text' && isBlockEmpty(after)) return { blocks: out, focusId: after.id }
  out.splice(end + 1, 0, b)
  return { blocks: out, focusId: b.id }
}
